/**
 * Displays a modal used for selecting and sending an email template
 * to a list of selected sites' emails.
 *
 * @param {boolean} props.isVisible - Controls modal visibility.
 * @param {Function} [props.resetModalFn] - Function to reset/close the modal.
 * @param {Object} props.selectedEmailItems - Map of site IDs to arrays of selected email IDs. {siteId<guid>: Array<EmailObjectId>}[]
 * @param {Array} props.siteData - Array of site objects containing email and other information.
 * @returns {JSX.Element}
 */
import { Col, Modal, Row, Spin, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { DraggableTitle, DraggableWrapper } from "@ui/modals/draggable-wrapper";
import Loader from "@ui/feedback/loader";
import RecipientsList from "./recipients-list";
import SiteEmailTemplateForm from "./site-email-template-form";

import { getMessageTemplates, getTemplateFields } from "@redux/actionCreators/bookingSetup";

import constants from "@constants/constants";

import "@app/features/system-setup/ui/message-templates/message-template-modal/styles.scss";
import "./send-email-template-modal.scss";

const { Title } = Typography;
const { NULLIFY } = constants;

const SendEmailByTemplateToSiteModal = ({
  isVisible,
  selectedEmailItems,
  siteData,
  resetModalFn = () => {}
}) => {
  const dispatch = useDispatch();

  const [searchedSiteEmailMapState, setSearchedSiteEmailMapState] = useState({});
  const [siteEmailMapState, setSiteEmailMapState] = useState({});

  const bookingSetupReducer = useSelector(state => state.bookingSetupReducer || null, shallowEqual);

  const {
    addMessageTemplate: {
      isLoading: addMessageTemplateLoading,
      resultData: addMessageTemplateData
    },
    sendMessageTemplateEmail: { isLoading: sendMessageTemplateEmailLoading },
    getMessageTemplates: { isLoading: getMessageTemplatesLoading },
    getTemplateFields: { isLoading: getTemplateFieldsLoading }
  } = bookingSetupReducer;

  const searchedSitesEmailMap = useMemo(() => {
    const siteEmailMap = {};
    siteData?.forEach(site => {
      const emails =
        site?.ClientList?.EmailAddressList?.map(contact => ({
          EmailObjectId: contact.EmailObjectId,
          EmailAddress: contact.email_address
        })) || [];
      if (emails.length) {
        siteEmailMap[site.ObjectId] = { emails, siteName: site.Name };
      }
    });
    return siteEmailMap;
  }, [siteData]);

  const siteEmailMapSourceOfTruth = useMemo(() => {
    const result = {};
    if (!selectedEmailItems || !searchedSiteEmailMapState) {
      return result;
    }
    Object.keys(selectedEmailItems).forEach(siteId => {
      const siteEntry = searchedSiteEmailMapState[siteId];
      if (!siteEntry) {
        return;
      }
      const selectedIds = selectedEmailItems[siteId] || [];
      if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
        return;
      }
      const index = {};
      siteEntry.emails.forEach(email => {
        index[email.EmailObjectId] = email;
      });
      const selectedEmails = selectedIds.map(id => index[id]).filter(Boolean);
      if (selectedEmails.length) {
        result[siteId] = { emails: selectedEmails, siteName: siteEntry.siteName };
      }
    });
    return result;
  }, [selectedEmailItems, searchedSiteEmailMapState]);

  const EMPTY = useMemo(() => [], []);

  const onParentCheckboxChange = useCallback(
    (e, siteObjectId) => {
      const next = e.target.checked ? siteEmailMapSourceOfTruth[siteObjectId] || EMPTY : EMPTY;
      setSiteEmailMapState(prev => {
        if (prev[siteObjectId] === next) {
          return prev;
        }
        return {
          ...prev,
          [siteObjectId]: { siteName: prev[siteObjectId].siteName, emails: next.emails || [] }
        };
      });
    },
    [siteEmailMapSourceOfTruth, EMPTY]
  );

  const onChildCheckboxChange = useCallback(
    (siteObjectId, checkedValues) => {
      setSiteEmailMapState(prev => {
        const selectableIds = prev[siteObjectId]?.emails.map(e => e.EmailObjectId) || [];
        if (selectableIds.length === checkedValues.length) {
          return prev;
        }
        const allEmails = siteEmailMapSourceOfTruth[siteObjectId]?.emails || [];
        const nextEmails = allEmails.filter(email => checkedValues.includes(email.EmailObjectId));
        return {
          ...prev,
          [siteObjectId]: { siteName: prev[siteObjectId].siteName, emails: nextEmails }
        };
      });
    },
    [siteEmailMapSourceOfTruth]
  );

  useEffect(() => {
    setSearchedSiteEmailMapState(searchedSitesEmailMap);
    setSiteEmailMapState(siteEmailMapSourceOfTruth);
  }, [searchedSitesEmailMap, siteEmailMapSourceOfTruth]);

  useEffect(() => {
    dispatch(getMessageTemplates());
    dispatch(getTemplateFields());
  }, [addMessageTemplateData]);

  const totalEmailCount = Object.keys(siteEmailMapState).reduce((acc, siteId) => {
    const siteEntry = siteEmailMapState[siteId];
    if (!siteEntry || !Array.isArray(siteEntry.emails)) {
      return acc;
    }
    return acc + siteEntry.emails.length;
  }, 0);

  return (
    <Modal
      title={<DraggableTitle title="Send group email" />}
      modalRender={modal => <DraggableWrapper>{modal}</DraggableWrapper>}
      open={isVisible}
      footer={NULLIFY}
      zIndex="1810"
      onCancel={() => resetModalFn()}
      maskClosable={false}
      width={"75%"}
      centered
      className="drs-modal-css send-template-email-candidate"
    >
      <Spin
        indicator={<Loader />}
        spinning={
          getMessageTemplatesLoading ||
          getTemplateFieldsLoading ||
          sendMessageTemplateEmailLoading ||
          addMessageTemplateLoading
        }
        className="loaderGifPosition"
      >
        <Row gutter={24} className="mb-4">
          <Col span={7}>
            <Title className="p-4" level={4}>
              Recipients ({totalEmailCount})
            </Title>

            <RecipientsList
              siteEmailMapState={siteEmailMapState}
              siteEmailMapSourceOfTruth={siteEmailMapSourceOfTruth}
              onParentCheckboxChange={onParentCheckboxChange}
              onChildCheckboxChange={onChildCheckboxChange}
            />
          </Col>

          <Col span={17}>
            <SiteEmailTemplateForm
              siteEmailMapState={siteEmailMapState}
              resetModalFn={resetModalFn}
            />
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

export default React.memo(SendEmailByTemplateToSiteModal);